<template>
  <TransitionRoot as="template" :show="!!props.open">
    <Dialog as="div" class="relative z-30" @close="handleTaskDetailsClosed">
      <div class="fixed inset-0" />

      <div class="fixed-0 overflow-hidden">
        <div class="absolute overflow-hidden">
          <div class="fixed inset-y-0 right-0 flex max-w-full sm:pl-10">
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-200"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
            >
              <DialogPanel class="pointer-events-auto w-screen max-w-xl">
                <div
                  id="scroll-container"
                  class="flex h-full flex-col overflow-y-scroll bg-white border-l border-l-gray-200"
                >
                  <div class="flex flex-col">
                    <div
                      class="sticky top-0 w-full max-w-xl px-6 pt-6 pb-4 bg-white z-50 border-b border-b-gray-200"
                    >
                      <div class="flex justify-between items-center">
                        <div class="flex flex-wrap items-center gap-2">
                          <div class="flex flex-row gap-2 items-center mr-4">
                            <span class="font-medium text-xl">{{
                              t("admin.settings.details")
                            }}</span>
                            <Tooltip
                              v-if="!canUpdate"
                              :content="t('admin.settings.read_only')"
                            >
                              <Eye class="w-5 text-gray-600" />
                            </Tooltip>
                          </div>
                          <div
                            v-if="
                              hasPermission(
                                permissions.deleteRecurringTask,
                                task.tenant.id
                              )
                            "
                          >
                            <button
                              @click="handleDeleteTask"
                              class="border border-red-500 hover:bg-red-50 text-red-600 group flex items-center px-4 py-2 text-sm cursor-pointer font-medium rounded-md focus:ring-0 focus:outline-none"
                            >
                              <TrashOne
                                class="mr-3 h-5 w-5 text-red-500 group-hover:text-red-600"
                                aria-hidden="true"
                              />
                              <span>
                                {{ t("components.buttons.delete_task") }}
                              </span>
                            </button>
                          </div>
                        </div>
                        <div>
                          <button
                            type="button"
                            class="rounded-sm bg-white text-gray-400 hover:text-gray-900 focus:outline-none transition"
                            @click="handleTaskDetailsClosed"
                          >
                            <span class="sr-only">Close panel</span>
                            <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>

                      <Errors class="mt-4" />

                      <div class="mt-6 flex-col space-y-1">
                        <input
                          type="text"
                          :placeholder="t('validation.attributes.description')"
                          class="resize-none border-transparent focus:border-transparent focus:ring-0 p-0 font-medium text-lg w-full"
                          v-model="description"
                          autofocus
                          ref="descriptionInput"
                          :disabled="!canUpdate"
                        />
                        <button
                          v-if="isEditingDescription"
                          class="flex items-center font-medium space-x-1 text-gray-400 hover:text-gray-900 cursor-pointer w-auto"
                          @click="handleChangeDescription"
                        >
                          <Check class="w-3.5 h-3.5" />
                          <span class="text-sm">{{
                            t("components.change_description")
                          }}</span>
                        </button>
                      </div>
                    </div>
                    <div>
                      <TaskMedia
                        :media="props.task.photos"
                        :can-delete="canUpdate"
                        @delete="confirmDeleteMedia"
                      />
                      <div class="flex p-6" v-if="canUpdate">
                        <MediaSelector
                          :loading="isProcessingAddingMedia"
                          @selected="handleMediaAdded"
                          alt-style
                        />
                      </div>
                    </div>
                    <div class="flex flex-col space-y-8 p-6">
                      <div>
                        <div class="flex items-center">
                          <span
                            class="font-semibold text-gray-500 text-sm w-32"
                            >{{ t("admin.search.filters.space") }}</span
                          >
                          <LocationDropdown
                            class="grow"
                            disable-ungrouped
                            disable-tenants
                            :tenants="filteredTenants"
                            :model-value="props.task.space"
                            :disabled="!canUpdate"
                            @update:model-value="handleSpaceSelected"
                          />
                        </div>
                      </div>
                      <div>
                        <div class="flex items-center">
                          <span
                            class="font-semibold text-gray-500 text-sm w-32"
                            >{{ t("admin.search.filters.assignee") }}</span
                          >
                          <MultipleUsers
                            :tenant-id="props.task.tenant.id"
                            :user-ids="
                              props.task.assignees?.map(
                                (assignee) => assignee.id
                              )
                            "
                            :disabled="!canUpdate"
                            blank
                            @change="handleAssigneeSelected"
                            :horizontalLabelWidth="'8rem'"
                          />
                        </div>
                      </div>
                      <div>
                        <div class="flex items-center">
                          <span
                            class="font-semibold text-gray-500 text-sm w-32"
                            >{{ t("validation.attributes.start_at") }}</span
                          >
                          <DueDate
                            blank
                            :schedule-date="props.task.scheduled_for"
                            @change="handleDueDateSelected"
                            :disabled="!canUpdate"
                          />
                        </div>
                      </div>
                      <div>
                        <div class="flex items-center">
                          <span
                            class="font-semibold text-gray-500 text-sm w-32"
                            >{{ t("admin.search.filters.category") }}</span
                          >
                          <CategorySelector
                            :category-id="props.task.category?.id"
                            :tenant-id="props.task.tenant.id"
                            @change="handleCategorySelected"
                            :disabled="!canUpdate"
                            :horizontalLabelWidth="'8rem'"
                          />
                        </div>
                      </div>
                      <div>
                        <div class="flex items-center">
                          <span
                            class="font-semibold text-gray-500 text-sm w-32"
                            >{{ t("admin.search.filters.priority") }}</span
                          >
                          <DropdownSelector
                            @change="handlePrioritySelected"
                            :value="props.task.priority.id"
                            :options="priorities"
                            :label="t('admin.search.filters.category')"
                            :disabled="!canUpdate"
                            :horizontalLabelWidth="'8rem'"
                          >
                            <template v-slot:icon>
                              <ExclamationTriangleIcon
                                class="h-5 w-5 text-gray-500 group-hover:text-gray-900"
                              />
                            </template>
                          </DropdownSelector>
                        </div>
                      </div>
                    </div>

                    <hr />

                    <div>
                      <div class="flex flex-col p-6">
                        <span
                          class="font-semibold text-gray-700 text-base w-32"
                          >{{
                            t("admin.settings.recurring_tasks.recurrence")
                          }}</span
                        >
                        <RRuleBuilder
                          :rule="parseRRuleString"
                          class="py-4"
                          @change="handleRRuleChanged"
                          :disabled="!canUpdate"
                        />
                        <div v-if="canUpdate">
                          <primary-button
                            @click="handleRRuleUpdated"
                            :label="
                              t(
                                'admin.settings.recurring_tasks.update_frequency'
                              )
                            "
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <hr />
                      <div>
                        <div class="flex flex-col p-6 gap-2">
                          <div class="flex justify-between">
                            <span class="font-semibold text-gray-700 text-base">
                              {{ t("admin.settings.checklist.title") }}
                            </span>
                          </div>
                          <TextAreaWithLabel
                            id="checklist_items"
                            :modelValue="checklistItems"
                            @update:modelValue="checklistItems = $event"
                            :placeholder="
                              t('admin.settings.checklist.input.placeholder')
                            "
                            :rows="3"
                            :disabled="!canCreateChecklist || !canUpdate"
                            @blur="handleChangeChecklistItems"
                          />
                          <div class="text-gray-700 text-sm">
                            {{
                              t("admin.settings.checklist.input.instructions")
                            }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <template v-if="canUpdate || props.task.documents?.length">
                      <hr />

                      <div class="flex flex-col space-y-2 p-6">
                        <FileInput
                          ref="hiddenFileUpload"
                          @input="uploadFiles"
                          multiple
                          hidden
                        />

                        <div class="flex justify-between">
                          <span class="font-semibold text-gray-700 text-base">{{
                            t("admin.settings.documents.documents")
                          }}</span>
                          <div v-if="canUpdate">
                            <secondary-button
                              :label="t('components.buttons.new_document')"
                              :disabled="isUploadingFiles"
                              @click="hiddenFileUpload?.click()"
                            />
                          </div>
                        </div>

                        <Errors :errors="hiddenFileUpload?.errors" />
                        <FileList
                          :files="props.task.documents"
                          @delete="deleteFile"
                          :disabled="!canUpdate"
                        />
                      </div>
                    </template>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
      <confirmation-modal
        :open="!!mediaBeingDeleted"
        @confirm="deleteMedia"
        @close="mediaBeingDeleted = null"
      />
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, defineEmits, defineProps, inject, ref, watch } from "vue";
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/20/solid";
import DueDate from "@/Tailwind/Components/Input/DueDate";
import CategorySelector from "@/Tailwind/Components/Input/CategorySelector";
import DropdownSelector from "@/Tailwind/Components/Input/DropdownSelector.vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import MediaSelector from "@/Tailwind/Components/MediaSelector";
import TrashOne from "@/Tailwind/Icon/TrashOne";
import { useUTC } from "@/Tailwind/Mixins/useUTC";
import Tooltip from "@/Tailwind/Components/Tooltip.vue";
import Eye from "@/Tailwind/Icon/Eye.vue";

import { useI18n } from "vue-i18n";
import { usePage } from "@inertiajs/vue3";
import { includeHours } from "@/Tailwind/Helpers/time";
import RRuleBuilder from "@/Tailwind/Components/RRule/RRuleBuilder.vue";
import { format, parseISO } from "date-fns";
import { RRule } from "rrule";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import Errors from "@/Tailwind/Components/Errors.vue";
import Check from "@/Tailwind/Icon/Check.vue";
import MultipleUsers from "@/Tailwind/Components/Input/MultipleUsers.vue";
import SecondaryButton from "@/Tailwind/Components/Buttons/SecondaryButton.vue";
import axios from "axios";
import FileList from "@/Tailwind/Components/FileList.vue";
import { FileInput } from "@/Components/Inputs";
import { ExclamationTriangleIcon } from "@heroicons/vue/24/outline";
import LocationDropdown from "@/Tailwind/Components/Input/LocationDropdown.vue";
import ConfirmationModal from "@/Tailwind/Components/ConfirmationModal.vue";
import TaskMedia from "@/Tailwind/Components/Tasks/Detail/TaskMedia.vue";
import TextAreaWithLabel from "@/Components/Inputs/TextAreaWithLabel.vue";

// Emitter
const emitter = inject("emitter");

const { t } = useI18n();

const props = defineProps({
  /** Whether the task detail view should be open */
  open: Boolean,

  /** The current task being viewed. */
  task: Object,
});

const { hasPermission, tenantsWithUserPermission, permissions } =
  inject("permissions");

const filteredTenants = computed(() =>
  usePage().props.tenants.filter((tenant) => {
    // Check if other tenants have the correct permission
    // Changing a task from one tenant to another requires the create permission
    const otherTenants =
      tenantsWithUserPermission(permissions.createRecurringTask) ?? [];

    // Include the owner tenant if the user has the correct permission
    const currentTenant = hasPermission(
      permissions.updateRecurringTask,
      props.task.tenant.id
    )
      ? props.task.tenant.id
      : null;

    return [...otherTenants, currentTenant].includes(tenant.id);
  })
);

const canUpdate = computed(() => {
  return hasPermission(permissions.updateRecurringTask, props.task?.tenant?.id);
});

watch(
  () => props.task,
  () => {
    description.value = props.task?.description;
    checklistItems.value = props.task?.checklist_items;
  }
);

const description = ref(props.task?.description);
const checklistItems = ref(props.task?.checklist_items);

const isEditingDescription = ref(false);

const checklistItemsArray = computed(() =>
  checklistItems.value
    .split(";")
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
);

watch(description, (value) => {
  if (value === props.task?.description) {
    isEditingDescription.value = false;
    return;
  }

  isEditingDescription.value = true;
});

const emit = defineEmits([
  /** A task has been updated. */
  "task-updated",

  /** The details pane should be closed. */
  "close",
]);

const { toUTC } = useUTC();

const parseRRuleString = computed(() => {
  const startDate = format(
    parseISO(props.task.scheduled_for),
    "yyyyMMdd'T'HHmmss"
  );

  const rule =
    "DTSTART:" + startDate + ";\n" + "RRULE:" + props.task.rrule.rule;

  return RRule.fromString(rule);
});

/** Determine which actions are currently being processed */
const isProcessingAddingMedia = ref(false);

const hiddenFileUpload = ref();
const isUploadingFiles = ref(false);

const mediaBeingDeleted = ref(null);

const confirmDeleteMedia = (id) => {
  mediaBeingDeleted.value = id;
};

const deleteMedia = () => {
  router.delete(
    route("tasks.recurring.photo.delete", [mediaBeingDeleted.value])
  );
  mediaBeingDeleted.value = null;
};

const uploadFiles = async (files) => {
  if (!files || !files.length) {
    return;
  }

  const data = new FormData();
  files.forEach((file, i) => {
    data.append(`file[${i}]`, file, file.name);
  });

  const { data: response } = await axios.post(
    route("api.documents.store", [props.task.tenant.id]),
    data
  );

  await updateFiles([
    ...props.task.documents.map((file) => file.id),
    ...response.data.map((file) => file.id),
  ]);
};

const deleteFile = async (fileId) => {
  await updateFiles([
    ...props.task.documents
      .filter((file) => file.id !== fileId)
      .map((file) => file.id),
  ]);
};

const updateFiles = async (fileIds) => {
  await router.put(
    route("settings.organisation.recurring-tasks.update", [props.task.id]),
    {
      tenant_id: props.task.tenant.id,
      space_id: props.task.space?.id,
      priority: props.task.priority?.id,
      category_id: props.task.category?.id,
      assignee_id: props.task.assignees?.map((assignee) => assignee.id),
      rrule: props.task.rrule.rule,
      start_date: props.task.scheduled_for
        ? toUTC(props.task.scheduled_for)
        : null,
      description: props.task.description,
      document_ids: fileIds,
      checklist_items: checklistItemsArray.value,
    },
    {
      onSuccess: () => {
        emit("task-updated");
        isUploadingFiles.value = false;
      },
    }
  );
};

const newRule = ref(null);
function handleRRuleChanged(rule) {
  newRule.value = rule;
}

const canCreateChecklist = computed(() => {
  return hasPermission(permissions.createChecklist, props.task.tenant.id);
});

function handleRRuleUpdated() {
  router.put(
    route("settings.organisation.recurring-tasks.update", [props.task.id]),
    {
      tenant_id: props.task.tenant.id,
      space_id: props.task.space?.id,
      priority: props.task.priority?.id,
      category_id: props.task.category?.id,
      assignee_id: props.task.assignees?.map((assignee) => assignee.id),
      rrule: newRule.value.value.toString(),
      start_date: props.task.scheduled_for
        ? toUTC(props.task.scheduled_for)
        : null,
      description: props.task.description,
      document_ids: props.task.documents?.map((document) => document.id),
      checklist_items: checklistItemsArray.value,
    },
    {
      onSuccess: () => {
        emit("task-updated");
      },
    }
  );
}

function handleChangeDescription() {
  router.put(
    route("settings.organisation.recurring-tasks.update", [props.task.id]),
    {
      tenant_id: props.task.tenant.id,
      space_id: props.task.space?.id,
      priority: props.task.priority?.id,
      category_id: props.task.category?.id,
      assignee_id: props.task.assignees?.map((assignee) => assignee.id),
      rrule: props.task.rrule.rule,
      start_date: props.task.scheduled_for
        ? toUTC(props.task.scheduled_for)
        : null,
      description: description.value,
      document_ids: props.task.documents?.map((document) => document.id),
      checklist_items: checklistItemsArray.value,
    },
    {
      onSuccess: () => {
        isEditingDescription.value = false;
        emit("task-updated");
      },
    }
  );
}

function handleChangeChecklistItems() {
  router.put(
    route("settings.organisation.recurring-tasks.update", [props.task.id]),
    {
      tenant_id: props.task.tenant.id,
      space_id: props.task.space?.id,
      priority: props.task.priority?.id,
      category_id: props.task.category?.id,
      assignee_id: props.task.assignees?.map((assignee) => assignee.id),
      rrule: props.task.rrule.rule,
      start_date: props.task.scheduled_for
        ? toUTC(props.task.scheduled_for)
        : null,
      description: description.value,
      document_ids: props.task.documents?.map((document) => document.id),
      checklist_items: checklistItemsArray.value,
    },
    {
      onSuccess: () => {
        emit("task-updated");
      },
    }
  );
}

/**
 * Delete the current task being viewed.
 */
function handleDeleteTask() {
  router.delete(
    route("settings.organisation.recurring-tasks.destroy", {
      recurring_task: props.task.id,
    }),
    {
      onSuccess: () => {
        emitter.emit("reload-recurring_tasks");
      },
    }
  );
}

/**
 * Add the media item(s) the user selected to the currently
 * active task.
 */
function handleMediaAdded(media) {
  router.post(
    route("settings.organisation.recurring-tasks.update", [props.task.id]),
    {
      // router.put doesn't work here because media is included in the request.
      _method: "PUT",

      tenant_id: props.task.tenant.id,
      space_id: props.task.space?.id,
      priority: props.task.priority?.id,
      category_id: props.task.category?.id,
      assignee_id: props.task.assignees?.map((assignee) => assignee.id),
      rrule: props.task.rrule.rule,
      start_date: props.task.scheduled_for
        ? toUTC(props.task.scheduled_for)
        : null,
      description: props.task.description,
      photos: media,
      document_ids: props.task.documents?.map((document) => document.id),
      checklist_items: checklistItemsArray.value,
    },
    {
      onStart: () => {
        isProcessingAddingMedia.value = true;
      },
      onSuccess: () => {
        emit("task-updated");
      },
      onFinish: () => {
        isProcessingAddingMedia.value = false;
      },
    }
  );
}

function handleTaskDetailsClosed() {
  emit("close");
}

/**
 * Update the assignee of the current task.
 */
function handleAssigneeSelected(users) {
  router.put(
    route("settings.organisation.recurring-tasks.update", [props.task.id]),
    {
      tenant_id: props.task.tenant.id,
      space_id: props.task.space?.id,
      priority: props.task.priority?.id,
      category_id: props.task.category?.id,
      assignee_id: users.map((user) => user.id),
      rrule: props.task.rrule.rule,
      start_date: props.task.scheduled_for
        ? toUTC(props.task.scheduled_for)
        : null,
      description: props.task.description,
      document_ids: props.task.documents?.map((document) => document.id),
      checklist_items: checklistItemsArray.value,
    },
    {
      onSuccess: () => {
        emit("task-updated");
      },
    }
  );
}

const priorities = usePage().props.priorities?.map((priority) => ({
  label: priority.name,
  value: priority.id,
}));

function handlePrioritySelected(priorityId) {
  router.put(
    route("settings.organisation.recurring-tasks.update", [props.task.id]),
    {
      tenant_id: props.task.tenant.id,
      space_id: props.task.space?.id,
      priority: priorityId,
      category_id: props.task.category?.id,
      assignee_id: props.task.assignees?.map((assignee) => assignee.id),
      rrule: props.task.rrule.rule,
      start_date: props.task.scheduled_for
        ? toUTC(props.task.scheduled_for)
        : null,
      description: props.task.description,
      document_ids: props.task.documents?.map((document) => document.id),
      checklist_items: checklistItemsArray.value,
    },
    {
      onSuccess: () => {
        emit("task-updated");
      },
    }
  );
}

/**
 * Update the category of the current task.
 */
function handleCategorySelected(categoryId) {
  router.put(
    route("settings.organisation.recurring-tasks.update", [props.task.id]),
    {
      tenant_id: props.task.tenant.id,
      space_id: props.task.space?.id,
      priority: props.task.priority?.id,
      category_id: categoryId,
      assignee_id: props.task.assignees?.map((assignee) => assignee.id),
      rrule: props.task.rrule.rule,
      start_date: props.task.scheduled_for
        ? toUTC(props.task.scheduled_for)
        : null,
      description: props.task.description,
      document_ids: props.task.documents?.map((document) => document.id),
      checklist_items: checklistItemsArray.value,
    },
    {
      onSuccess: () => {
        emit("task-updated");
      },
    }
  );
}

/**
 * Update the space of the current task.
 */
function handleSpaceSelected(space) {
  if (!space) {
    return;
  }

  router.put(
    route("settings.organisation.recurring-tasks.update", [props.task.id]),
    {
      tenant_id: props.task.tenant.id,
      space_id: space?.id,
      priority: props.task.priority?.id,
      category_id: props.task.category?.id,
      assignee_id: props.task.assignees?.map((assignee) => assignee.id),
      rrule: props.task.rrule.rule,
      start_date: props.task.scheduled_for
        ? toUTC(props.task.scheduled_for)
        : null,
      description: props.task.description,
      document_ids: props.task.documents?.map((document) => document.id),
      checklist_items: checklistItemsArray.value,
    },
    {
      onSuccess: () => {
        emit("task-updated");
      },
    }
  );
}

/**
 * Update the due date of the current task.
 */
function handleDueDateSelected(date) {
  router.put(
    route("settings.organisation.recurring-tasks.update", [props.task.id]),
    {
      tenant_id: props.task.tenant.id,
      space_id: props.task.space?.id,
      priority: props.task.priority?.id,
      category_id: props.task.category?.id,
      assignee_id: props.task.assignees?.map((assignee) => assignee.id),
      rrule: props.task.rrule.rule,
      start_date: date ? toUTC(includeHours(date).toISOString()) : null,
      description: props.task.description,
      document_ids: props.task.documents?.map((document) => document.id),
      checklist_items: checklistItemsArray.value,
    },
    {
      onSuccess: () => {
        emit("task-updated");
      },
    }
  );
}
</script>
